import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Tables } from '@/integrations/supabase/types'
import { useTranslation } from 'react-i18next'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EduMessage {
    id: string
    role: 'user' | 'assistant'
    content: string
    created_at: string
    metadata?: Record<string, unknown>
}

export interface EduUpdate {
    lesson_complete: boolean
    concept_understood: string | null
    weak_concept_flagged: string | null
    new_current_unit: number | null
    new_current_lesson: number | null
    trigger_quiz: boolean
    current_concept_index?: number   // 0-indexed; emitted every turn, never decreases within a lesson
    suggested_chips?: string[]
    forced?: boolean                 // true when server forced advance or completion
}


export interface QuizQuestion {
    id: string
    question: string
    options: string[]
    correct_index: number
    explanation: string
    difficulty: 'standard' | 'stretch'
}

export interface QuizReviewItem {
    question: string
    options: string[]
    correct: string
    student_answer: string
    correct_yn: boolean
    explanation: string
}

export interface QuizResultPayload {
    score: number
    passed: boolean
    passingScore: number
    new_mastery_score: number
    lesson_complete: boolean
    module_completed: boolean
    message: string
    details: { questionId: string; correct: boolean; concept: string }[]
    review: QuizReviewItem[]
}

export interface EduQuiz {
    quiz_id: string
    category_id: string
    questions: QuizQuestion[]
    generated_at: string
}

export interface EduSessionState {
    conversationId: string | null
    messages: EduMessage[]
    isLoading: boolean
    isStreaming: boolean
    error: string | null
    quiz: EduQuiz | null
    isQuizOpen: boolean
    currentUnit: number
    currentLesson: number
    masteryScore: number
    quizPassed: boolean
    lastEduUpdate: EduUpdate | null
    // sessionComplete: true = AI finished teaching this lesson.
    // The page shows "Start Quiz" button. Quiz does NOT auto-fire.
    sessionComplete: boolean
    // quizReady: true = quiz fetch is in progress (show spinner on button)
    quizReady: boolean
    suggestedChips: string[]
    lastQuizResult: QuizResultPayload | null
    hasMoreMessages: boolean
    isLoadingEarlier: boolean
    currentConceptIndex: number
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEducationSession(
    categoryId: string,
    classId?: string | null,
    initialConversationId?: string | null
) {
    const { user, userProfile } = useAuth()
    const { i18n } = useTranslation()
    const supabase = createClientComponentClient()

    const [state, setState] = useState<EduSessionState>({
        conversationId: initialConversationId || null,
        messages: [],
        isLoading: false,
        isStreaming: false,
        error: null,
        quiz: null,
        isQuizOpen: false,
        currentUnit: 1,
        currentLesson: 1,
        masteryScore: 0,
        quizPassed: false,
        lastEduUpdate: null,
        sessionComplete: false,
        quizReady: false,
        suggestedChips: [],
        lastQuizResult: null,
        hasMoreMessages: false,
        isLoadingEarlier: false,
        currentConceptIndex: 0,
    })

    const isLoadingRef = useRef(false)
    const isStreamingRef = useRef(false)

    useEffect(() => { isLoadingRef.current = state.isLoading }, [state.isLoading])
    useEffect(() => { isStreamingRef.current = state.isStreaming }, [state.isStreaming])

    // Ref pattern: keeps userProfile fresh inside the sendMessage callback without
    // adding it to useCallback deps (which would recreate sendMessage on every auth
    // state change and risk aborting in-flight streams).
    const userProfileRef = useRef(userProfile)
    useEffect(() => { userProfileRef.current = userProfile }, [userProfile])

    const streamBufferRef = useRef<string>('')
    const displayLenRef = useRef(0)
    const rafRef = useRef<number | null>(null)
    const parsedGuideModeRef = useRef<string | null>(null)
    const abortRef = useRef<AbortController | null>(null)
    const hasInitialised = useRef(false)

    const startSmoothReveal = useCallback((assistantMsgId: string, getBuf: () => string) => {
        if (rafRef.current !== null) return
        const tick = () => {
            const buf = getBuf()
            const visibleBuf = buf
                .split('EDU_UPDATE')[0]
                .split('\nPARENT:')[0]
                .split('\nGUIDE:')[0]
                .trimEnd()
            if (displayLenRef.current < visibleBuf.length) {
                const remaining = visibleBuf.length - displayLenRef.current
                const step = Math.max(3, Math.ceil(remaining / 12))
                displayLenRef.current = Math.min(visibleBuf.length, displayLenRef.current + step)
                const shown = visibleBuf.slice(0, displayLenRef.current)
                setState(s => ({
                    ...s,
                    messages: s.messages.map(m =>
                        m.id === assistantMsgId
                            ? {
                                ...m,
                                content: shown,
                                ...(parsedGuideModeRef.current ? { metadata: { ...(m.metadata || {}), guide_mode: parsedGuideModeRef.current } } : {})
                            }
                            : m
                    ),
                }))
            }
            if (displayLenRef.current < visibleBuf.length) {
                rafRef.current = requestAnimationFrame(tick)
            } else {
                rafRef.current = null
            }
        }
        rafRef.current = requestAnimationFrame(tick)
    }, [])

    // ── Initialise: get or create conversation ─────────────────────────────────
    const initialise = useCallback(async () => {
        if (!user || !categoryId || hasInitialised.current) return
        hasInitialised.current = true

        setState(s => ({ ...s, isLoading: true, error: null }))

        try {
            let convId = initialConversationId

            if (!convId) {
                let query = supabase
                    .from('conversations')
                    .select('id, metadata')
                    .eq('user_id', user.id)
                    .eq('category_id', categoryId)
                    .contains('metadata', { session_type: 'education' })
                    .order('created_at', { ascending: false })

                // If searching for a conversation in a specific class context
                if (classId) {
                    query = query.contains('metadata', { class_id: classId })
                }

                let { data: existing } = await query.limit(1)

                if (!existing || existing.length === 0) {
                    // Thread-guard: Check if an education conversation exists for this user + category
                    // without the class_id filter (prevents split threads if an earlier version saved with
                    // null/different class_id format)
                    const { data: fallbackConv } = await supabase
                        .from('conversations')
                        .select('id, metadata')
                        .eq('user_id', user.id)
                        .eq('category_id', categoryId)
                        .contains('metadata', { session_type: 'education' })
                        .order('created_at', { ascending: false })
                        .limit(1)

                    if (fallbackConv && fallbackConv.length > 0) {
                        existing = fallbackConv
                        const meta = (fallbackConv[0].metadata && typeof fallbackConv[0].metadata === 'object' && !Array.isArray(fallbackConv[0].metadata))
                            ? (fallbackConv[0].metadata as Record<string, any>)
                            : {}
                        // Unify thread: update metadata to add class_id so future exact matches succeed
                        if (classId && meta.class_id !== classId) {
                            await supabase
                                .from('conversations')
                                .update({
                                    metadata: {
                                        ...meta,
                                        class_id: classId,
                                    }
                                })
                                .eq('id', fallbackConv[0].id)
                        }
                    }
                }

                if (existing && existing.length > 0) {
                    convId = existing[0].id
                } else {
                    const { data: newConv, error: createErr } = await supabase
                        .from('conversations')
                        .insert({
                            user_id: user.id,
                            category_id: categoryId,
                            title: `Education Session`,
                            metadata: {
                                session_type: 'education',
                                class_id: classId ?? null,
                            },
                        })
                        .select('id')
                        .single()

                    if (createErr) throw createErr
                    convId = newConv.id
                }
            }

            const { data: msgs } = await supabase
                .from('messages')
                .select('id, role, content, created_at, metadata')
                .eq('conversation_id', convId)
                .order('created_at', { ascending: false })
                .order('id', { ascending: false })
                .limit(200)

            const fetched = (msgs || []) as EduMessage[]
            const loadedMsgs = [...fetched].reverse()

            let progQuery = supabase
                .from('student_subject_progress')
                .select('current_unit, current_lesson, mastery_score')
                .eq('user_id', user.id)
                .eq('category_id', categoryId)
                .limit(1)
            if (classId) progQuery = progQuery.eq('class_id', classId)
            const { data: progress } = await progQuery.maybeSingle()

            const progressData = progress

            const lastEduMsg = [...loadedMsgs].reverse().find(
                (m) => m.role === 'assistant' && (m.metadata as Record<string, any>)?.edu_update
            )
            const initialConceptIndex =
                ((lastEduMsg?.metadata as Record<string, any>)?.edu_update?.current_concept_index as number) ?? 0

            setState(s => ({
                ...s,
                conversationId: convId!,
                messages: loadedMsgs,
                hasMoreMessages: fetched.length === 200,
                isLoading: false,
                currentUnit: progressData?.current_unit ?? 1,
                currentLesson: progressData?.current_lesson ?? 1,
                masteryScore: progressData?.mastery_score ?? 0,
                currentConceptIndex: initialConceptIndex,
            }))
        } catch (err) {
            setState(s => ({
                ...s,
                isLoading: false,
                error: (err as Error).message || 'Failed to start session',
            }))
        }
    }, [user, categoryId, classId, initialConversationId, supabase])

    // ── Load earlier messages (upward pagination) ──────────────────────────────
    const loadEarlierMessages = useCallback(async () => {
        if (!state.conversationId || state.isLoadingEarlier || !state.hasMoreMessages) return
        if (state.messages.length === 0) return

        setState(s => ({ ...s, isLoadingEarlier: true }))
        try {
            const oldestCreatedAt = state.messages[0]?.created_at
            const { data: olderMsgs, error } = await supabase
                .from('messages')
                .select('id, role, content, created_at, metadata')
                .eq('conversation_id', state.conversationId)
                .lt('created_at', oldestCreatedAt)
                .order('created_at', { ascending: false })
                .order('id', { ascending: false })
                .limit(100)

            if (error) throw error

            const fetched = (olderMsgs || []) as EduMessage[]
            fetched.reverse() // chronological order

            setState(s => ({
                ...s,
                isLoadingEarlier: false,
                messages: [...fetched, ...s.messages],
                hasMoreMessages: fetched.length === 100,
            }))
        } catch (err) {
            console.error('[useEducationSession] Failed to load earlier messages:', err)
            setState(s => ({ ...s, isLoadingEarlier: false }))
        }
    }, [state.conversationId, state.isLoadingEarlier, state.hasMoreMessages, state.messages, supabase])

    useEffect(() => { initialise() }, [initialise])

    // ── Request quiz explicitly — the ONLY quiz entry point ────────────────────
    // Called by the "Start Quiz" button in EducationSessionPage.
    // Never called automatically — that was the bug causing quiz at lesson 2 start.
    const requestQuiz = useCallback(async () => {
        if (!user || !state.conversationId || isLoadingRef.current || isStreamingRef.current) return

        setState(s => ({ ...s, quizReady: true, isLoading: true }))

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.access_token) throw new Error('Not authenticated')

            const supabaseUrl = (supabase as any).supabaseUrl as string

            const quizRes = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    conversation_id: state.conversationId,
                    message: '[quiz_request]',
                    category_id: categoryId,
                    class_id: classId ?? null,
                    session_type: 'education',
                    language: i18n.language,
                    current_module: state.currentUnit,
                    current_lesson: state.currentLesson,
                }),
            })

            if (!quizRes.ok) throw new Error(`Quiz request failed: ${quizRes.status}`)

            const quizJson = await quizRes.json()
            if (quizJson.type === 'quiz' && quizJson.quiz) {
                setState(s => ({
                    ...s,
                    isLoading: false,
                    quizReady: false,
                    quiz: quizJson.quiz,
                    isQuizOpen: true,
                    sessionComplete: false,   // hide the "Start Quiz" button while quiz is open
                }))
            } else {
                throw new Error('Invalid quiz response from server')
            }
        } catch (err) {
            setState(s => ({
                ...s,
                isLoading: false,
                quizReady: false,
                error: (err as Error).message || 'Failed to load quiz. Please try again.',
            }))
        }
    }, [user?.id, state.conversationId, categoryId, classId, supabase])

    // ── Send message ───────────────────────────────────────────────────────────
    const sendMessage = useCallback(async (text: string) => {
        if (!user || !state.conversationId || isLoadingRef.current || isStreamingRef.current) return
        if (!text.trim()) return

        const QUIZ_INTENT = /^(start|take|begin|do)\s+(the\s+)?quiz\b|^quiz\s*(time|now|please)?$|ready\s+for\s+(the\s+)?quiz/i
        if (QUIZ_INTENT.test(text.trim())) {
            requestQuiz()
            return
        }

        const isInternal = text.trim().startsWith('[') && text.trim().endsWith(']')
        const userMsg: EduMessage = {
            id: `temp-${Date.now()}`,
            role: 'user',
            content: text.trim(),
            created_at: new Date().toISOString(),
        }

        setState(s => ({
            ...s,
            quizPassed: false,
            sessionComplete: false,   // reset on each new send so chips update
            suggestedChips: [],       // clear chips while busy
            messages: isInternal ? s.messages : [...s.messages, userMsg],
            isLoading: true,
            isStreaming: false,
            error: null,
        }))

        abortRef.current?.abort()
        abortRef.current = new AbortController()
        streamBufferRef.current = ''

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.access_token) throw new Error('Not authenticated')

            const supabaseUrl = (supabase as any).supabaseUrl as string

            // Read guide_mode flag fresh from ref — avoids stale closure when
            // the header toggle fires between renders without a sendMessage remount.
            const isGuide = !!((userProfileRef.current as any)?.preferences?.guide_mode
                            ?? (userProfileRef.current as any)?.preferences?.parent_guide)
            const response = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    conversation_id: state.conversationId,
                    message: text.trim(),
                    category_id: categoryId,
                    class_id: classId ?? null,
                    session_type: 'education',
                    language: i18n.language,
                    current_module: state.currentUnit,
                    current_lesson: state.currentLesson,
                    guide_mode: isGuide,
                }),
                signal: abortRef.current.signal,
            })

            if (!response.ok) throw new Error(`AI service error: ${response.status}`)

            const contentType = response.headers.get('content-type') || ''

            // Non-streaming JSON (shouldn't happen for normal messages but handle gracefully)
            if (contentType.includes('application/json')) {
                const json = await response.json()
                if (json.type === 'quiz' && json.quiz) {
                    setState(s => ({ ...s, isLoading: false, quiz: json.quiz, isQuizOpen: true }))
                    return
                }
            }

            // ── Streaming SSE ──────────────────────────────────────────────────────
            setState(s => ({ ...s, isLoading: false, isStreaming: true }))

            streamBufferRef.current = ''
            displayLenRef.current = 0
            parsedGuideModeRef.current = null
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }

            const assistantMsgId = `stream-${Date.now()}`
            setState(s => ({
                ...s,
                messages: [...s.messages, {
                    id: assistantMsgId,
                    role: 'assistant',
                    content: '',
                    created_at: new Date().toISOString(),
                }],
            }))

            requestAnimationFrame(() => {
                document.getElementById(`msg-${assistantMsgId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })
            })

            const reader = response.body!.getReader()
            const decoder = new TextDecoder()
            let sseBuffer = ''
            let receivedDone = false

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                const combined = sseBuffer + decoder.decode(value, { stream: true })
                const lines = combined.split('\n')
                sseBuffer = lines.pop() || '' // hold possibly-incomplete last line

                for (const line of lines) {
                    const trimmed = line.trim()
                    if (!trimmed.startsWith('data: ')) continue
                    const data = trimmed.slice(6)
                    if (data === '[DONE]') continue

                    try {
                        const parsed = JSON.parse(data)

                        if (parsed.content) {
                            streamBufferRef.current += parsed.content
                            startSmoothReveal(assistantMsgId, () => streamBufferRef.current)
                        }

                        if (parsed.guideMode) {
                            parsedGuideModeRef.current = parsed.guideMode
                        }

                        if (parsed.done) {
                            receivedDone = true
                            if (parsed.guideMode) {
                                parsedGuideModeRef.current = parsed.guideMode
                            }
                            if (rafRef.current !== null) {
                                cancelAnimationFrame(rafRef.current)
                                rafRef.current = null
                            }
                            const full = streamBufferRef.current
                                .split('EDU_UPDATE')[0]
                                .split('\nPARENT:')[0]
                                .split('\nGUIDE:')[0]
                                .trimEnd()
                            const eduUpdate = parsed.eduUpdate as EduUpdate | undefined

                            // KEY DESIGN: trigger_quiz=true does NOT auto-fire the quiz.
                            // It sets sessionComplete=true → "Start Quiz" button appears.
                            // Student must click it → requestQuiz() is called → quiz loads.
                            // This is what eliminates the lesson-2-quiz-at-start bug.
                            setState(s => ({
                                ...s,
                                isStreaming: false,
                                lastEduUpdate: eduUpdate ?? s.lastEduUpdate,
                                sessionComplete: eduUpdate?.lesson_complete ?? false,
                                currentConceptIndex: eduUpdate?.current_concept_index ?? s.currentConceptIndex,
                                suggestedChips: eduUpdate?.suggested_chips || [],
                                messages: s.messages.map(m =>
                                    m.id === assistantMsgId
                                        ? {
                                            ...m,
                                            content: full,
                                            metadata: {
                                                ...(m.metadata || {}),
                                                ...(parsedGuideModeRef.current ? { guide_mode: parsedGuideModeRef.current } : {}),
                                                ...(eduUpdate ? { edu_update: eduUpdate } : {})
                                            }
                                        }
                                        : m
                                )
                            }))

                            // Refresh progress from DB on lesson completion
                            if (eduUpdate?.lesson_complete || eduUpdate?.new_current_lesson) {
                                try {
                                    let progQuery = supabase
                                        .from('student_subject_progress')
                                        .select('mastery_score, current_unit, current_lesson')
                                        .eq('user_id', user.id)
                                        .eq('category_id', categoryId)
                                        .limit(1)
                                    if (classId) progQuery = progQuery.eq('class_id', classId)
                                    const { data: prog } = await progQuery.maybeSingle()

                                    if (prog) {
                                        setState(s => ({
                                            ...s,
                                            masteryScore: prog.mastery_score ?? s.masteryScore,
                                            currentUnit: prog.current_unit ?? s.currentUnit,
                                            currentLesson: prog.current_lesson ?? s.currentLesson,
                                        }))
                                    }
                                } catch { /* non-fatal */ }
                            }
                        }
                    } catch { /* skip malformed chunks */ }
                }
            }

            // Safety net: Guarantee input is never permanently blocked even if done event was missed on network boundary
            if (!receivedDone) {
                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current)
                    rafRef.current = null
                }
                const full = streamBufferRef.current
                    .split('EDU_UPDATE')[0]
                    .split('\nPARENT:')[0]
                    .split('\nGUIDE:')[0]
                    .trimEnd()
                setState(s => ({
                    ...s,
                    isStreaming: false,
                    messages: s.messages.map(m =>
                        m.id === assistantMsgId
                            ? {
                                ...m,
                                content: full,
                                metadata: {
                                    ...(m.metadata || {}),
                                    ...(parsedGuideModeRef.current ? { guide_mode: parsedGuideModeRef.current } : {})
                                }
                            }
                            : m
                    )
                }))
            }
        } catch (err) {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
            if ((err as Error).name === 'AbortError') return
            setState(s => {
                // Remove empty assistant placeholder so no empty bubble remains
                const cleanedMessages = s.messages.filter(m => !(m.id.startsWith('stream-') && !m.content.trim()))
                return {
                    ...s,
                    messages: cleanedMessages,
                    isLoading: false,
                    isStreaming: false,
                    error: (err as Error).message || 'Failed to send message',
                }
            })
        }
    }, [user?.id, state.conversationId, categoryId, classId, supabase])

    // ── Submit quiz answers ────────────────────────────────────────────────────
    const submitQuizAnswers = useCallback(async (answers: Record<string, number>): Promise<QuizResultPayload | null> => {
        if (!user || !state.conversationId || !state.quiz) return null

        setState(s => ({ ...s, isLoading: true }))

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.access_token) throw new Error('Not authenticated')

            const supabaseUrl = (supabase as any).supabaseUrl as string

            const response = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    conversation_id: state.conversationId,
                    message: '[quiz_submission]',
                    category_id: categoryId,
                    class_id: classId ?? null,
                    session_type: 'education',
                    quiz_answers: answers,
                    language: i18n.language,
                }),
            })

            if (!response.ok) {
                const errorJson = await response.json().catch(() => ({}))
                throw new Error(errorJson.error || `Quiz submission failed: ${response.status}`)
            }

            const result: QuizResultPayload = await response.json()

            const resultMsg: EduMessage = {
                id: `quiz-result-${Date.now()}`,
                role: 'assistant',
                content: result.message || `Quiz complete! Score: ${result.score ?? '?'}%`,
                created_at: new Date().toISOString(),
                metadata: { quiz_result: result },
            }

            setState(s => ({
                ...s,
                isLoading: false,
                quizPassed: result.passed ?? false,
                sessionComplete: false,
                suggestedChips: result.passed
                    ? ['Start next lesson', 'Review key concepts']
                    : ['Try quiz again', 'Review lesson concepts'],
                messages: [...s.messages, resultMsg],
                masteryScore: result.new_mastery_score ?? s.masteryScore,
                lastQuizResult: result,
            }))

            // Refresh progress from DB — quiz submission advances lesson server-side
            try {
                let progQuery = supabase
                    .from('student_subject_progress')
                    .select('mastery_score, current_unit, current_lesson')
                    .eq('user_id', user.id)
                    .eq('category_id', categoryId)
                    .limit(1)
                if (classId) progQuery = progQuery.eq('class_id', classId)
                const { data: prog } = await progQuery.maybeSingle()
                if (prog) {
                    setState(s => ({
                        ...s,
                        currentUnit: prog.current_unit ?? s.currentUnit,
                        currentLesson: prog.current_lesson ?? s.currentLesson,
                    }))
                }
            } catch { /* non-fatal */ }

            return result
        } catch (err) {
            setState(s => ({
                ...s,
                isLoading: false,
                error: (err as Error).message,
            }))
            return null
        }
    }, [user?.id, state.conversationId, state.quiz, categoryId, classId, supabase, i18n.language])

    const dismissQuiz = useCallback(() => {
        setState(s => ({ ...s, isQuizOpen: false, quiz: null }))
    }, [])

    const clearError = useCallback(() => {
        setState(s => ({ ...s, error: null }))
    }, [])

    const forceNextConcept = useCallback(() => {
        sendMessage('[force_next_concept]')
    }, [sendMessage])

    return {
        ...state,
        sendMessage,
        requestQuiz,       // NEW: called by "Start Quiz" button — the only quiz trigger
        forceNextConcept,  // Manual concept override (Guide Mode)
        submitQuizAnswers,
        dismissQuiz,
        clearError,
        loadEarlierMessages,
        reinitialise: () => {
            hasInitialised.current = false
            initialise()
        },
    }
}